/**
 * BinauralPicker — premium picker for layering binaural beats under any
 * Private Suite session. 5 brainwave bands × 2 variants (Pure / Pad).
 *
 * Each band has a `sampleSeconds = 8s` audition just like MusicSelection.
 * Aesthetic mirrors MusicSelection (deep navy → near-black, gold accents,
 * Cormorant Garamond headings) so the experience feels of-a-piece.
 *
 * Props:
 *   current        Currently chosen beat `{ id, label, url }` or null.
 *   onSelect(beat) Called when the user taps a variant. The picker DOES NOT
 *                  auto-close; the consumer can close on its own.
 *   onClose()      Called when the user taps Back or the close X.
 */
import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, Pause, Play, Check, Waves } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

// 5 brainwave bands × 2 variants — files generated by
// /app/backend/scripts/generate_binaural_beats.py and served via
// /api/audio/binaural/<id>.m4a (StaticFiles mount in server.py).
const BANDS = [
  {
    id: 'delta',
    label: 'Delta',
    hz: '1–4 Hz',
    vibe: 'Deep sleep, restoration',
  },
  {
    id: 'theta',
    label: 'Theta',
    hz: '4–8 Hz',
    vibe: 'Meditation, healing, intuition',
  },
  {
    id: 'alpha',
    label: 'Alpha',
    hz: '8–13 Hz',
    vibe: 'Calm focus, flow',
  },
  {
    id: 'beta',
    label: 'Beta',
    hz: '13–30 Hz',
    vibe: 'Alertness, productivity',
  },
  {
    id: 'gamma',
    label: 'Gamma',
    hz: '30 Hz+',
    vibe: 'Peak performance, clarity',
  },
];

const VARIANTS = [
  { id: 'pure', name: 'I',  suffix: 'pure', subtitle: 'Pure tones' },
  { id: 'pad',  name: 'II', suffix: 'pad',  subtitle: 'With soft pad' },
];

const SAMPLE_SECONDS = 8;

const BinauralPicker = ({ current, onClose, onSelect }) => {
  const audioRef = useRef(null);
  const sampleTimerRef = useRef(null);
  const sampleTickRef = useRef(null);
  const [samplingId, setSamplingId] = useState(null);
  const [sampleProgress, setSampleProgress] = useState(0);

  // Hard teardown on unmount.
  useEffect(() => {
    const cleanup = () => {
      const a = audioRef.current;
      if (a) { a.pause(); a.src = ''; a.load && a.load(); }
      if (sampleTimerRef.current) clearTimeout(sampleTimerRef.current);
      if (sampleTickRef.current) clearInterval(sampleTickRef.current);
      sampleTimerRef.current = null;
      sampleTickRef.current = null;
    };
    window.addEventListener('pagehide', cleanup);
    return () => { window.removeEventListener('pagehide', cleanup); cleanup(); };
  }, []);

  const stopSample = () => {
    const a = audioRef.current;
    if (a) a.pause();
    if (sampleTimerRef.current) clearTimeout(sampleTimerRef.current);
    if (sampleTickRef.current) clearInterval(sampleTickRef.current);
    sampleTimerRef.current = null;
    sampleTickRef.current = null;
    setSamplingId(null);
    setSampleProgress(0);
  };

  const handleSample = (band, variant) => {
    const id = `${band.id}_${variant.suffix}`;
    if (samplingId === id) { stopSample(); return; }
    stopSample();
    const a = audioRef.current;
    if (!a) return;
    a.src = `${API}/api/audio/binaural/${id}.m4a`;
    a.currentTime = 0;
    a.volume = 0.4;  // demo it at the volume it'd play at during a session
    a.play().then(() => {
      setSamplingId(id);
      setSampleProgress(0);
      const start = Date.now();
      sampleTickRef.current = setInterval(() => {
        const p = Math.min(1, (Date.now() - start) / (SAMPLE_SECONDS * 1000));
        setSampleProgress(p);
      }, 100);
      sampleTimerRef.current = setTimeout(() => stopSample(), SAMPLE_SECONDS * 1000);
    }).catch(() => stopSample());
  };

  const choose = (band, variant) => {
    const id = `${band.id}_${variant.suffix}`;
    const beat = {
      id,
      label: `${band.label} ${variant.name}`,
      url: `/api/audio/binaural/${id}.m4a`,
    };
    stopSample();
    onSelect(beat);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex flex-col"
      style={{ background: 'linear-gradient(180deg, #1f3a5c 0%, #0c1a30 55%, #03060f 100%)' }}
      data-testid="binaural-picker"
    >
      <audio ref={audioRef} />

      <div className="flex-1 overflow-y-auto px-6 py-8">
        <motion.button
          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          onClick={onClose}
          className="flex items-center gap-2 text-white/45 hover:text-white/65 transition-colors mb-8"
          data-testid="binaural-back"
        >
          <ChevronLeft size={20} />
          <span className="text-sm">Back</span>
        </motion.button>

        <motion.div
          initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <p className="text-[10px] uppercase tracking-[0.5em] text-amber-300/55 mb-3 flex items-center justify-center gap-2">
            <Waves size={11} /> Binaural Beats
          </p>
          <h1
            className="text-4xl font-light text-white mb-3 tracking-wide"
            style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}
          >
            Layer a brainwave
          </h1>
          <p className="text-white/45 text-sm italic max-w-sm mx-auto">
            Use headphones for the full effect. {SAMPLE_SECONDS}s sample on tap.
          </p>
        </motion.div>

        <div className="space-y-3">
          {BANDS.map((band, bi) => {
            const isActive = current && current.id.startsWith(`${band.id}_`);
            return (
              <motion.div
                key={band.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + bi * 0.05 }}
                className={`rounded-2xl px-4 py-3.5 transition-all ${
                  isActive
                    ? 'bg-amber-300/10 border border-amber-300/35'
                    : 'bg-white/[0.04] border border-white/[0.07]'
                }`}
                data-testid={`binaural-band-${band.id}`}
              >
                <div className="flex items-baseline justify-between mb-2.5">
                  <div>
                    <p
                      className="text-white text-base font-medium tracking-wide"
                      style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: '1.15rem' }}
                    >
                      {band.label}
                      <span className="ml-2 text-[10px] tracking-[0.25em] text-amber-200/55">{band.hz}</span>
                    </p>
                    <p className="text-white/40 text-[11px] italic">{band.vibe}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  {VARIANTS.map((v) => {
                    const id = `${band.id}_${v.suffix}`;
                    const sampling = samplingId === id;
                    const isChosen = current?.id === id;
                    return (
                      <div
                        key={v.id}
                        className={`flex-1 rounded-xl px-3 py-2.5 flex items-center justify-between gap-2 transition-all ${
                          isChosen
                            ? 'bg-amber-300/15 border border-amber-300/45'
                            : 'bg-white/[0.04] border border-white/[0.08]'
                        }`}
                        data-testid={`binaural-variant-${id}`}
                      >
                        <button
                          onClick={() => handleSample(band, v)}
                          className="relative w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{ background: sampling ? 'rgba(241,210,156,0.25)' : 'rgba(255,255,255,0.08)' }}
                          data-testid={`binaural-sample-${id}`}
                          aria-label={sampling ? 'Stop sample' : 'Sample beat'}
                        >
                          {sampling && (
                            <svg className="absolute inset-0 -rotate-90" viewBox="0 0 36 36" width="36" height="36" aria-hidden>
                              <circle cx="18" cy="18" r="16" fill="none"
                                stroke="rgba(241,210,156,0.85)"
                                strokeWidth="2"
                                strokeDasharray={`${sampleProgress * 100.5} 100.5`}
                                strokeLinecap="round" />
                            </svg>
                          )}
                          {sampling
                            ? <Pause size={13} className="text-white relative z-10" />
                            : <Play size={13} className="text-white/85 ml-0.5 relative z-10" />}
                        </button>
                        <button
                          onClick={() => choose(band, v)}
                          className="flex-1 text-left text-[11px] uppercase tracking-[0.25em] text-white/70 hover:text-white transition-colors"
                          data-testid={`binaural-choose-${id}`}
                          aria-label={`Choose ${band.label} ${v.name}`}
                        >
                          {isChosen
                            ? <span className="text-amber-200">Chosen · {v.subtitle}</span>
                            : <>{v.subtitle}</>}
                        </button>
                        {isChosen && (
                          <div className="w-5 h-5 rounded-full bg-amber-300 flex items-center justify-center flex-shrink-0">
                            <Check size={10} className="text-[#1f3a5c]" />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      <div className="flex-shrink-0 p-6 pb-24 bg-gradient-to-t from-[#03060f] via-[#03060f]/95 to-transparent">
        <motion.button
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
          onClick={onClose}
          className="w-full py-4 rounded-2xl font-medium bg-gradient-to-r from-amber-300 to-amber-500 text-[#1f3a5c] shadow-lg shadow-amber-300/30"
          data-testid="binaural-done"
        >
          {current ? `Done · ${current.label}` : 'Continue without beats'}
        </motion.button>
      </div>
    </motion.div>
  );
};

export default BinauralPicker;
